import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState, useCallback, useEffect, useMemo } from "react";
import { type SortingState } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { LeadsHeader } from "shared/app/LeadsTableView/components/LeadsHeader";
import { LeadsTable } from "shared/app/LeadsTableView/slices/LeadsTable";
import { NoLeadsSlice } from "shared/app/LeadsTableView/slices/NoLeadsSlice";
import { CreateLeadDefaults } from "shared/app/LeadsTableView/components/CreateLeadModal";
import { Pagination } from "shared/components/Pagination";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { PaginatedResponse } from "shared/types";
import { Lead, LeadType } from "views/leads-id/types";
import {
  getLeadTypesParam,
  getSelectedLeadTypes,
  isAnyLeadTypeSelection,
  isOnlyOwnerSelected
} from "./utils/leadTypeFilter";
import { fetchStages, stagesQueryKey } from "views/settings-crm/api";
import {
  StageGroup,
  StageKind,
  type Stage
} from "views/settings-crm/types/stage";
import queryString from "qs";
import { FiltersDrawer } from "./components/FiltersDrawers";
import { Show } from "shared/components";
import { FilterBadge } from "shared/app/LeadsTableView/components/FilterBadge";
import { ColumnVisibilityItem, useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";
import { APPROVAL_LIKELIHOOD_BAND_LABEL } from "api/approval-likelihood";
import { getErrorMessage } from "utils/error";
import { useLeadsQueryParams } from "./hooks/useLeadsQueryParams";
import {
  CreatedWithinOption,
  LeadsFilters,
  LeadSavedView,
  RemovableFilterKey,
  ShowingWithinOption
} from "./types";
import { useLeadSavedViewsController } from "./components/SavedViewsControl/useLeadSavedViewsController";
import { SavedViewsModals } from "./components/SavedViewsControl/SavedViewsModals";
import {
  captureViewState,
  viewFiltersToLeadsFilters,
  viewSortingToSortingState
} from "./utils/savedViewState";
import { mergeColumnsWithDefaults } from "./utils/columnVisibility";
import { resolveColumnLabel } from "shared/app/ApplicationsTableView/components/ColumnsMenu/utils";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { buildLeadsSearchParams } from "./helpers";
import { LeadsPipelineView } from "./slices/LeadsPipeline";
import {
  DEFAULT_PER_PAGE,
  DEFAULT_SORTING,
  LEAD_SORT_COLUMN_MAP,
  LEADS_COLUMNS_TAB_KEY,
  getLeadsDefaultColumnVisibility,
  LeadColumnId,
  LeadSortColumnId,
  LEADS_PIPELINE_COLUMN_KEY,
  SESSION_STORAGE_COLUMNS_KEY,
  SESSION_STORAGE_PIPELINE_ORDER_KEY,
  LeadView
} from "./constants";

// Saved-views mode mirrors the ephemeral column visibility into sessionStorage
// so it survives a page reload (filters already persist via the URL). Without
// this, columns reset to default on reload and the live snapshot no longer
// matches the active saved view, so the control falls back to "Custom".
const readStoredColumns = (): ColumnVisibilityItem[] | undefined => {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_COLUMNS_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    // Validate each element's shape before casting: mergeColumnsWithDefaults
    // maps over `col.id`, so a corrupt/legacy blob with null or primitive
    // entries would throw during render.
    if (
      !Array.isArray(parsed) ||
      !parsed.every(
        (col) => col && typeof col === "object" && typeof col.id === "string"
      )
    )
      return undefined;
    return parsed as ColumnVisibilityItem[];
  } catch {
    return undefined;
  }
};

const persistColumns = (columns: ColumnVisibilityItem[]): void => {
  try {
    sessionStorage.setItem(
      SESSION_STORAGE_COLUMNS_KEY,
      JSON.stringify(columns)
    );
  } catch {
    // Storage unavailable (private mode / quota) — fall back to default reload
    // behavior; the active view will just read as "Custom" after a reload.
  }
};

const readStoredStageOrder = (): string[] | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_PIPELINE_ORDER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every((id) => typeof id === "string"))
      return null;
    return parsed as string[];
  } catch {
    return null;
  }
};

const persistStageOrder = (stageOrder: string[] | null): void => {
  try {
    if (!stageOrder?.length) {
      sessionStorage.removeItem(SESSION_STORAGE_PIPELINE_ORDER_KEY);
      return;
    }
    sessionStorage.setItem(
      SESSION_STORAGE_PIPELINE_ORDER_KEY,
      JSON.stringify(stageOrder)
    );
  } catch {
    // Storage unavailable — the board order just resets on reload.
  }
};

// Stable empty default so an undefined stages query doesn't hand LeadsTable a
// fresh [] each render (which would defeat its memoized columns).
const EMPTY_STAGES: Stage[] = [];

const getOrderDirection = (sort?: SortingState[number]) => {
  if (!sort) return undefined;
  return sort.desc ? "desc" : "asc";
};

const getCreatedWithinLabel = (filters: LeadsFilters): string => {
  if (filters.createdWithin === CreatedWithinOption.Last7Days)
    return "Last 7 days";
  if (filters.createdWithin === CreatedWithinOption.Last30Days)
    return "Last 30 days";
  const after = filters.createdAfter?.slice(5).replace("-", "/") ?? "";
  const before = filters.createdBefore?.slice(5).replace("-", "/") ?? "";
  if (filters.createdAfter === filters.createdBefore) return after;
  return `${after} - ${before}`;
};

const getShowingWithinLabel = (filters: LeadsFilters): string => {
  if (filters.showingWithin === ShowingWithinOption.Last7Days)
    return "Last 7 days";
  if (filters.showingWithin === ShowingWithinOption.Last30Days)
    return "Last 30 days";
  if (filters.showingWithin === ShowingWithinOption.Next7Days)
    return "Next 7 days";
  if (filters.showingWithin === ShowingWithinOption.Next30Days)
    return "Next 30 days";
  const after = filters.showingAfter?.slice(5).replace("-", "/") ?? "";
  const before = filters.showingBefore?.slice(5).replace("-", "/") ?? "";
  if (filters.showingAfter === filters.showingBefore) return after;
  return `${after} - ${before}`;
};

export const LeadsTableView = ({
  hasHeaderTitle = false,
  property_ids,
  unit_ids,
  contact_id,
  viewPortHeightOffset = 120,
  createLeadDefaults,
  persistParams = false
}: {
  hasHeaderTitle?: boolean;
  property_ids?: string[];
  unit_ids?: string[];
  contact_id?: number;
  viewPortHeightOffset?: number;
  createLeadDefaults?: CreateLeadDefaults;
  persistParams?: boolean;
}) => {
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const {
    filters,
    searchParams,
    sorting,
    assigneeIds: selectedAssigneeIds,
    view,
    isInitialized,
    setFilters,
    setSearchParams,
    setSorting,
    setAssigneeIds: setSelectedAssigneeIds,
    setView,
    applyState
  } = useLeadsQueryParams({ persistParams });

  const starredFilter = searchParams.starred === "true";

  const [debouncedSearch] = useDebouncedValue(searchParams.search, 500);
  const [showFilters, setShowFilters] = useState(false);

  const queryClient = useQueryClient();
  const { auth, refreshAuthUserDashboardState, type, partner } =
    useAuthContext();
  const { Property } = useUnitsPropertiesDictionary();
  const isOwnerLeadsEnabled = type.isOwnerLeads;
  const isLeadScoringEnabled =
    partner?.features.includes(FEATURES.CRM_AI_LEAD_SCORING) ?? false;

  // When saved views are enabled (BoomCRM), column visibility is ephemeral
  // working state: it is not persisted to the user, only captured into a saved
  // view. Non-CRM partners keep the legacy per-user column persistence.
  const savedViewsEnabled = type.isBoomCRM;

  // Owner-leads partners get an extra togglable "Type" column in the visibility
  // panel and saved views; everyone else keeps the base column set.
  const defaultColumnVisibility = useMemo(
    () =>
      getLeadsDefaultColumnVisibility(
        isOwnerLeadsEnabled,
        isLeadScoringEnabled
      ),
    [isOwnerLeadsEnabled, isLeadScoringEnabled]
  );

  const [columnsVisibility, setColumnsVisibility] = useState<
    ColumnVisibilityItem[]
  >(() => {
    if (savedViewsEnabled)
      return persistParams
        ? mergeColumnsWithDefaults(readStoredColumns(), defaultColumnVisibility)
        : defaultColumnVisibility;
    return mergeColumnsWithDefaults(
      auth?.columns_visibility?.[LEADS_COLUMNS_TAB_KEY],
      defaultColumnVisibility
    );
  });

  const [pipelineStageOrder, setPipelineStageOrder] = useState<string[] | null>(
    () => (savedViewsEnabled && persistParams ? readStoredStageOrder() : null)
  );

  const [totalColumnsVisibility, setTotalColumnsVisibility] = useState<
    Record<string, ColumnVisibilityItem[]>
  >(auth?.columns_visibility || {});

  useEffect(() => {
    if (savedViewsEnabled) return;
    setColumnsVisibility(
      mergeColumnsWithDefaults(
        totalColumnsVisibility[LEADS_COLUMNS_TAB_KEY],
        defaultColumnVisibility
      )
    );
  }, [totalColumnsVisibility, savedViewsEnabled, defaultColumnVisibility]);

  const updateColumnVisibility = useMutation({
    mutationKey: ["LEADS_COLUMN_VISIBILITY", "UPDATE"],
    mutationFn: (columnsVisibilityPayload: Record<string, unknown>) =>
      instance.patch(`/portal/v1/users/${auth?.id}`, {
        columns_visibility: columnsVisibilityPayload
      })
  });

  const applyColumns = useCallback(
    async (nextColumns: ColumnVisibilityItem[]) => {
      setColumnsVisibility(nextColumns);

      // Ephemeral mode: keep column changes local (persisted into a saved view
      // via getSnapshot), but mirror to sessionStorage on the standalone /leads
      // page so the working state — and thus the active view — survives reload.
      if (savedViewsEnabled) {
        if (persistParams) persistColumns(nextColumns);
        return;
      }

      const updatedTotal = {
        ...totalColumnsVisibility,
        [LEADS_COLUMNS_TAB_KEY]: nextColumns
      };
      setTotalColumnsVisibility(updatedTotal);

      try {
        await updateColumnVisibility.mutateAsync(updatedTotal);
        refreshAuthUserDashboardState();
      } catch (error) {
        toast.error(
          `Failed to update column visibility: ${getErrorMessage(error)}`
        );
      }
    },
    [
      savedViewsEnabled,
      persistParams,
      totalColumnsVisibility,
      updateColumnVisibility,
      refreshAuthUserDashboardState
    ]
  );

  const handleColumnToggle = (id: string, checked: boolean) => {
    void applyColumns(
      columnsVisibility.map((col) =>
        col.id === id ? { ...col, checked } : col
      )
    );
  };

  const getSnapshot = useCallback(
    () =>
      captureViewState({
        filters,
        search: searchParams.search,
        starred: starredFilter,
        sorting,
        assigneeIds: selectedAssigneeIds,
        view,
        columnsVisibility,
        stageOrder: pipelineStageOrder
      }),
    [
      filters,
      searchParams.search,
      starredFilter,
      sorting,
      selectedAssigneeIds,
      view,
      columnsVisibility,
      pipelineStageOrder
    ]
  );

  const applyView = useCallback(
    (view: LeadSavedView | null) => {
      if (!view) {
        applyState({
          filters: undefined,
          search: "",
          starred: false,
          sorting: DEFAULT_SORTING,
          assigneeIds: null,
          view: LeadView.List
        });
        void applyColumns(defaultColumnVisibility);
        setPipelineStageOrder(null);
        persistStageOrder(null);
        return;
      }

      const viewFilters = view.filters ?? {};
      applyState({
        filters: viewFiltersToLeadsFilters(viewFilters),
        search: viewFilters.search ?? "",
        starred: Boolean(viewFilters.starred),
        sorting: viewSortingToSortingState(viewFilters),
        assigneeIds: viewFilters.assignee_ids?.length
          ? viewFilters.assignee_ids
          : null,
        view:
          viewFilters.view === LeadView.Pipeline
            ? LeadView.Pipeline
            : LeadView.List
      });
      void applyColumns(
        mergeColumnsWithDefaults(
          view.columns?.[LEADS_COLUMNS_TAB_KEY],
          defaultColumnVisibility
        )
      );

      const nextStageOrder = viewFilters.pipeline_stage_order ?? null;
      setPipelineStageOrder(nextStageOrder);
      persistStageOrder(nextStageOrder);
    },
    [applyState, applyColumns, defaultColumnVisibility]
  );

  const defaultSnapshot = useMemo(
    () =>
      captureViewState({
        filters: undefined,
        search: "",
        starred: false,
        sorting: DEFAULT_SORTING,
        assigneeIds: null,
        view: LeadView.List,
        columnsVisibility: defaultColumnVisibility,
        stageOrder: null
      }),
    [defaultColumnVisibility]
  );

  const handleViewSaved = useCallback(
    () => persistStageOrder(pipelineStageOrder),
    [pipelineStageOrder]
  );

  const savedViewsController = useLeadSavedViewsController({
    enabled: type.isBoomCRM,
    getSnapshot,
    applyView,
    defaultSnapshot,
    onViewSaved: handleViewSaved
  });

  const propertyIds = useMemo(
    () => filters?.properties?.map((p) => p.id),
    [filters?.properties]
  );
  const stageIds = useMemo(
    () => filters?.stages?.map((s) => s.id),
    [filters?.stages]
  );
  const leadSourceIds = useMemo(
    () => filters?.leadSources?.map((ls) => ls.id),
    [filters?.leadSources]
  );
  const leadTypes = filters?.leadTypes;

  const selectedLeadTypes = useMemo(
    () => getSelectedLeadTypes(leadTypes, isOwnerLeadsEnabled, persistParams),
    [leadTypes, isOwnerLeadsEnabled, persistParams]
  );
  const onlyOwnerSelected = isOnlyOwnerSelected(selectedLeadTypes);
  const pipelineDisabled =
    isOwnerLeadsEnabled && isAnyLeadTypeSelection(selectedLeadTypes);
  const effectiveLeadTypes = getLeadTypesParam(selectedLeadTypes);
  const effectiveFilters = useMemo(
    () => ({ ...filters, leadTypes: effectiveLeadTypes }),
    [filters, effectiveLeadTypes]
  );

  // The Property interested column is force-hidden for Owner-only selections,
  // so drop its toggle from the columns menu without touching stored visibility.
  // Alphabetized for the menu only — the stored order is what saved views keep.
  const menuColumnsVisibility = useMemo(() => {
    const visibleColumns = onlyOwnerSelected
      ? columnsVisibility.filter(
          (col) => col.id !== LeadColumnId.PropertyInterested
        )
      : columnsVisibility;
    return visibleColumns.toSorted((a, b) =>
      resolveColumnLabel(a.label, Property).localeCompare(
        resolveColumnLabel(b.label, Property)
      )
    );
  }, [columnsVisibility, onlyOwnerSelected, Property]);

  const handleStageOrderChange = useCallback((nextOrder: string[]) => {
    setPipelineStageOrder(nextOrder);
  }, []);

  const setLeadTypes = useCallback(
    (nextLeadTypes: LeadType[]) => {
      setFilters((prev) => ({
        ...prev,
        leadTypes:
          nextLeadTypes.length === 0
            ? [LeadType.Owner, LeadType.Tenant]
            : nextLeadTypes
      }));
    },
    [setFilters]
  );

  useEffect(() => {
    if (persistParams && pipelineDisabled && view === LeadView.Pipeline)
      setView(LeadView.List);
  }, [persistParams, pipelineDisabled, view, setView]);

  const removeFilter = useCallback(
    <K extends RemovableFilterKey>(key: K, id: string) => {
      setFilters((prev) => {
        const arr = prev?.[key];
        if (!arr?.length) return prev;
        return {
          ...prev,
          [key]:
            key === "activityTypes" ||
            key === "leadTypes" ||
            key === "approvalLikelihoodBands"
              ? (arr as string[]).filter((item) => item !== id)
              : (arr as { id: string; name: string }[]).filter(
                  (item) => item.id !== id
                )
        };
      });
    },
    [setFilters]
  );

  const {
    data: queryLeadsData,
    refetch: refetchLeads,
    isFetching
  } = useQuery({
    queryKey: [
      "LEADS_TABLE",
      searchParams.page,
      searchParams.per_page,
      debouncedSearch,
      selectedAssigneeIds,
      propertyIds,
      stageIds,
      leadSourceIds,
      effectiveLeadTypes,
      contact_id,
      filters?.activityTypes,
      filters?.approvalLikelihoodBands,
      filters?.onWaitlist,
      filters?.createdWithin,
      filters?.createdAfter,
      filters?.createdBefore,
      filters?.showingWithin,
      filters?.showingAfter,
      filters?.showingBefore,
      starredFilter,
      sorting
    ],
    queryFn: async () =>
      await instance
        .get(
          "/portal/v1/leads?" +
            queryString.stringify(
              buildLeadsSearchParams({
                page: searchParams.page,
                perPage: Number(searchParams.per_page) || 10,
                propertyIds: property_ids || propertyIds,
                unitIds: unit_ids,
                stageIds,
                sourceIds: leadSourceIds,
                assigneeIds: selectedAssigneeIds,
                filters: effectiveFilters,
                search: debouncedSearch,
                starred: starredFilter,
                contactId: contact_id,
                orderBy: sorting[0]
                  ? LEAD_SORT_COLUMN_MAP[sorting[0].id as LeadSortColumnId]
                  : undefined,
                orderDirection: getOrderDirection(sorting[0]),
                isLeadScoringEnabled
              }),
              { arrayFormat: "brackets" }
            )
        )
        .then((res) => res.data as PaginatedResponse<Lead>),
    // Wait for the sessionStorage restore and for the debounced search to
    // settle, otherwise restoring a saved search fires an unfiltered request
    enabled: isInitialized && debouncedSearch === searchParams.search
  });

  const refetchLeadsAndPipeline = useCallback(() => {
    void refetchLeads();
    queryClient.invalidateQueries({ queryKey: [LEADS_PIPELINE_COLUMN_KEY] });
  }, [refetchLeads, queryClient]);

  const { data: queryStagesData } = useQuery({
    queryKey: stagesQueryKey(StageKind.Lead),
    queryFn: () => fetchStages(StageKind.Lead)
  });

  const { data: queryOwnerStagesData } = useQuery({
    queryKey: stagesQueryKey(StageKind.Lead, StageGroup.OwnerPipeline),
    queryFn: () => fetchStages(StageKind.Lead, StageGroup.OwnerPipeline),
    enabled: isOwnerLeadsEnabled
  });

  const hasActiveFilters =
    Object.entries(filters ?? {}).some(
      ([key, f]) => key !== "leadTypes" && Array.isArray(f) && f.length > 0
    ) ||
    Boolean(filters?.onWaitlist) ||
    (filters?.createdWithin != null &&
      filters.createdWithin !== CreatedWithinOption.None) ||
    (filters?.showingWithin != null &&
      filters.showingWithin !== ShowingWithinOption.None);
  const hasAssigneeFilter = Boolean(selectedAssigneeIds?.length);
  // Use the effective selection so the implicit Tenant default counts as an
  // active filter — an owner-only partner with zero tenant leads must still
  // get the header (and the Type popover) instead of the no-leads slice.
  const hasTypeFilter = Boolean(effectiveLeadTypes?.length);
  const hasSearch = Boolean(debouncedSearch.trim());

  const starredCount =
    (queryLeadsData?.extra as { starred_count?: number } | undefined)
      ?.starred_count ?? 0;

  const setStarredFilter = useCallback(
    (next: boolean) => {
      setSearchParams({ page: "1", starred: next ? "true" : "" });
    },
    [setSearchParams]
  );

  if (
    queryLeadsData?.items.length === 0 &&
    !hasSearch &&
    !isFetching &&
    !hasActiveFilters &&
    !hasAssigneeFilter &&
    !hasTypeFilter &&
    !starredFilter
  )
    return (
      <NoLeadsSlice
        hasHeaderTitle={hasHeaderTitle}
        setIsCreateLeadModalOpen={setIsCreateLeadModalOpen}
        refetchLeads={refetchLeadsAndPipeline}
        isCreateLeadModalOpen={isCreateLeadModalOpen}
        createLeadDefaults={createLeadDefaults}
      />
    );

  return (
    <>
      <LeadsHeader
        search={searchParams.search}
        hasHeaderTitle={hasHeaderTitle}
        isCreateLeadModalOpen={isCreateLeadModalOpen}
        refetchLeads={refetchLeadsAndPipeline}
        setIsCreateLeadModalOpen={setIsCreateLeadModalOpen}
        setSearch={(search) => setSearchParams({ page: "1", search })}
        selectedAssigneeIds={selectedAssigneeIds}
        setSelectedAssigneeIds={setSelectedAssigneeIds}
        setShowFilters={setShowFilters}
        createLeadDefaults={createLeadDefaults}
        starredFilter={starredFilter}
        setStarredFilter={setStarredFilter}
        starredCount={starredCount}
        columnsVisibility={menuColumnsVisibility}
        onColumnToggle={handleColumnToggle}
        savedViews={
          persistParams && type.isBoomCRM
            ? savedViewsController.controlProps
            : undefined
        }
        view={view}
        setView={persistParams ? setView : undefined}
        isOwnerLeadsEnabled={isOwnerLeadsEnabled}
        selectedLeadTypes={selectedLeadTypes}
        onSelectedLeadTypesChange={setLeadTypes}
        pipelineDisabled={pipelineDisabled}
      />
      <Show if={hasActiveFilters}>
        <div className="flex flex-wrap gap-2 mb-4">
          {filters?.properties?.map((property) => (
            <FilterBadge
              key={property.id}
              filter={property.name}
              onRemove={() => removeFilter("properties", property.id)}
            />
          ))}
          {filters?.stages?.map((stage) => (
            <FilterBadge
              key={stage.id}
              filter={stage.name}
              onRemove={() => removeFilter("stages", stage.id)}
            />
          ))}
          {filters?.leadSources?.map((leadSource) => (
            <FilterBadge
              key={leadSource.id}
              filter={leadSource.name}
              onRemove={() => removeFilter("leadSources", leadSource.id)}
            />
          ))}
          {filters?.activityTypes?.map((activityType) => (
            <FilterBadge
              key={activityType}
              filter={activityType}
              onRemove={() => removeFilter("activityTypes", activityType)}
            />
          ))}
          <Show if={isLeadScoringEnabled}>
            {filters?.approvalLikelihoodBands?.map((band) => (
              <FilterBadge
                key={band}
                filter={APPROVAL_LIKELIHOOD_BAND_LABEL[band]}
                onRemove={() => removeFilter("approvalLikelihoodBands", band)}
              />
            ))}
          </Show>
          <Show if={Boolean(filters?.onWaitlist)}>
            <FilterBadge
              filter="On the waitlist"
              onRemove={() =>
                setFilters((prev) => ({ ...prev, onWaitlist: false }))
              }
            />
          </Show>
          {filters?.createdWithin &&
            filters.createdWithin !== CreatedWithinOption.None && (
              <FilterBadge
                filter={getCreatedWithinLabel(filters)}
                onRemove={() =>
                  setFilters((prev) => ({
                    ...prev,
                    createdWithin: CreatedWithinOption.None,
                    createdAfter: undefined,
                    createdBefore: undefined
                  }))
                }
              />
            )}
          <Show
            if={
              filters?.showingWithin != null &&
              filters.showingWithin !== ShowingWithinOption.None
            }
          >
            <FilterBadge
              filter={filters ? getShowingWithinLabel(filters) : ""}
              onRemove={() =>
                setFilters((prev) => ({
                  ...prev,
                  showingWithin: ShowingWithinOption.None,
                  showingAfter: undefined,
                  showingBefore: undefined
                }))
              }
            />
          </Show>
        </div>
      </Show>
      <Show if={view === LeadView.List}>
        <LeadsTable
          stagesData={queryStagesData ?? EMPTY_STAGES}
          ownerStagesData={queryOwnerStagesData ?? EMPTY_STAGES}
          isOwnerLeadsEnabled={isOwnerLeadsEnabled}
          isLeadScoringEnabled={isLeadScoringEnabled}
          isLoading={isFetching}
          data={queryLeadsData?.items || []}
          perPageRows={Number(DEFAULT_PER_PAGE)}
          viewPortHeightOffset={viewPortHeightOffset}
          sorting={sorting}
          columnsVisibility={columnsVisibility}
          hidePropertyInterested={onlyOwnerSelected}
          onSortingChange={setSorting}
          pagination={
            <div className="flex items-center justify-between">
              <p className="font-medium text-content-1000 text-base">
                Total leads: {queryLeadsData?.total_count || 0}
              </p>
              <div className="ml-auto flex md:max-w-[calc(100vw-106px)]">
                <Pagination
                  className="md:justify-end justify-center"
                  total={queryLeadsData?.page_count || 0}
                  current={Number(searchParams.page)}
                  onChange={(p) => setSearchParams({ page: String(p) })}
                />
              </div>
            </div>
          }
        />
      </Show>
      <Show if={view === LeadView.Pipeline}>
        <LeadsPipelineView
          stagesData={queryStagesData ?? EMPTY_STAGES}
          ownerStagesData={queryOwnerStagesData ?? EMPTY_STAGES}
          filters={effectiveFilters}
          search={debouncedSearch}
          assigneeIds={selectedAssigneeIds}
          starred={starredFilter}
          enabled={isInitialized && debouncedSearch === searchParams.search}
          isLeadScoringEnabled={isLeadScoringEnabled}
          stageOrder={pipelineStageOrder}
          onStageOrderChange={handleStageOrderChange}
        />
      </Show>
      <FiltersDrawer
        property_ids={property_ids}
        unit_ids={unit_ids}
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        search={debouncedSearch}
        hidePropertyFilter={onlyOwnerSelected}
        isLeadScoringEnabled={isLeadScoringEnabled}
      />
      <Show if={type.isBoomCRM}>
        <SavedViewsModals {...savedViewsController.modalsProps} />
      </Show>
    </>
  );
};
